import type { FavouriteStationIds, Station, StationsResponse } from '@/types'
import { ref, computed } from 'vue'
import { useApi } from '@/lib/api'
import { useI18n } from 'vue-i18n'
import { confirmModal } from '@/lib/confirm-modal'

/** Interface for favourite stations adapter */
export interface FavouriteStationsAdapter {
  /** Does the adapter support syncing? */
  supportsSync: boolean

  /** Return station IDs */
  load: () => Promise<FavouriteStationIds>

  /** Save station IDs */
  save: (ids: FavouriteStationIds) => Promise<FavouriteStationIds>

  /** Add favourite */
  add: (id: number) => Promise<boolean>

  /** Remove */
  remove: (id: number) => Promise<boolean>
}

/** Previous occured error when resolving favourite stations */
const favouriteStationsError = ref<string | undefined>(undefined)

/** An array of favourite stations */
const favouriteStations = ref<Station[]>([])

/** An array of favourite stations ID's */
export const favouriteStationIds = ref<FavouriteStationIds>([
  ...JSON.parse(localStorage.getItem('viritinFavouriteStations') || '[]'),
])

/** API instance for favourite stations */
export const favouriteStationsApi = useApi()

/** Is favourite stations loading? */
export const favouriteStationsLoading = ref<boolean>(true)

/** Adapter to use */
export const adapter = ref<FavouriteStationsAdapter>()

/** Set adapter to use */
export const setAdapter = (value: FavouriteStationsAdapter) => {
  adapter.value = value
  initFavouriteStations()
}

export const favouriteStationsSyncSupported = computed<boolean>(() => {
  return adapter.value?.supportsSync === true
})

/** Initialize favourite stations */
const initFavouriteStations = async (silent: boolean = false) => {
  if (!adapter.value) {
    throw new Error('Missing favourite stations adapter')
  }

  if (silent === false) {
    favouriteStationsLoading.value = true
  }

  try {
    // Load station ID's
    favouriteStationIds.value = await adapter.value.load()

    // Resolve favourite stations by ID's
    await resolveFavouriteStations()
  } catch (_error) {
    favouriteStationsError.value = 'favourites.resolveError'
  } finally {
    favouriteStationsLoading.value = false
  }
}

/** Resolve stations by ID's */
const resolveFavouriteStations = async () => {
  if (!adapter.value) {
    throw new Error('Missing favourite stations adapter')
  }

  if (!favouriteStationIds.value.length) {
    favouriteStations.value = []
    return
  }

  favouriteStationsError.value = undefined

  try {
    const response = await favouriteStationsApi.ky
      .get('stations', {
        searchParams: {
          id: favouriteStationIds.value.join(' '),
        },
      })
      .json<StationsResponse>()

    favouriteStations.value = response.data || []
  } catch (_error) {
    favouriteStationsError.value = 'favourites.resolveError'
  }
}

/** Composable for using favourite stations functionality */
export const useFavouriteStations = () => {
  const { t } = useI18n()

  const toggleFavouriteStation = async (station: Station): Promise<boolean> => {
    if (!adapter.value) {
      throw new Error('Missing favourite stations adapter')
    }

    const idx = favouriteStationIds.value.indexOf(station.id)

    // Remove
    if (idx >= 0) {
      confirmModal({
        title: t('favourites.removeConfirm', {
          stationName: station.title,
        }),
        cancelButton: t('cancel'),
        confirmButton: t('remove'),
        confirm: async () => {
          const result = await adapter.value?.remove(station.id)
          if (result === true) {
            favouriteStationIds.value.splice(idx, 1)
            favouriteStations.value.splice(idx, 1)
            adapter.value?.save(favouriteStationIds.value)
          }
        },
      })

      return false
    }

    // Add
    const result = await adapter.value?.add(station.id)
    if (result === true) {
      favouriteStationIds.value.push(station.id)
      favouriteStations.value.push(station)
      adapter.value?.save(favouriteStationIds.value)
    }

    return true
  }

  return {
    favouriteStationsLoading,
    favouriteStationsApi,
    favouriteStationsError,
    favouriteStations,
    favouriteStationIds,
    favouriteStationsSyncSupported,
    initFavouriteStations,
    toggleFavouriteStation,
  }
}
